import { useNavigationBarQuery } from "@crowdcoursing/api/graphql";
import { Business as BusinessIcon } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import { useMemo, useState } from "react";

import { useOrganizations } from "~hooks/useOrganizations";
import { Route } from "~services/router/Route";
import { PageHeader } from "~ui/atoms/PageHeader/PageHeader";
import { SearchField } from "~ui/atoms/SearchField/SearchField";

import { CreateOrganizationDialog } from "../../organisms/Organizations/CreateOrganizationDialog";
import { MyOrganizationsTemplate } from "../../templates/MyOrganizationsTemplate/MyOrganizationsTemplate";

export const tMyOrganizations = "My Organizations";
export const tCreateOrganization = "Create";

export const MyOrganizationsPage = () => {
  const [isOrganizationDialogOpen, setIsOrganizationDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { data: navData } = useNavigationBarQuery();
  const isSiteAdmin = navData?.me.isSiteAdmin ?? false;

  const { organizations, isLoading, error } = useOrganizations();

  const filteredOrganizations = useMemo(() => {
    if (!organizations || !searchValue.trim()) {
      return organizations ?? [];
    }

    const searchTerm = searchValue.toLowerCase().trim();
    return organizations.filter((org) => {
      const orgName = org.name?.toLowerCase() ?? "";
      const orgDescription = org.description?.toLowerCase() ?? "";
      const orgAddress = org.address?.toLowerCase() ?? "";

      return orgName.includes(searchTerm) || orgDescription.includes(searchTerm) || orgAddress.includes(searchTerm);
    });
  }, [organizations, searchValue]);

  const handleOpenOrganizationDialog = () => setIsOrganizationDialogOpen(true);
  const handleCloseOrganizationDialog = () => setIsOrganizationDialogOpen(false);

  return (
    <Box sx={{ p: { sm: 2, xs: 1.5 } }}>
      <Stack spacing={2}>
        <PageHeader
          icon={<BusinessIcon data-variant="page-header" />}
          rightContent={
            isSiteAdmin ? (
              <Button onClick={handleOpenOrganizationDialog} variant="page-header">
                {tCreateOrganization}
              </Button>
            ) : undefined
          }
          title={tMyOrganizations}
        />
        <SearchField
          onChange={setSearchValue}
          placeholder="Search organizations..."
          value={searchValue}
          sx={{ mb: 1.5 }}
        />
        <MyOrganizationsTemplate
          data={filteredOrganizations}
          linkTo={(organization) => Route.organization({ organizationId: organization.id ?? "" })}
          onSearchChange={setSearchValue}
          searchValue={searchValue}
          showHeader={false}
          status={error ? "error" : isLoading ? "pending" : "success"}
        />
      </Stack>

      {isOrganizationDialogOpen && <CreateOrganizationDialog onClose={handleCloseOrganizationDialog} />}
    </Box>
  );
};
